import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { MUIDataTableColumn } from 'mui-datatables';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { grey } from '@mui/material/colors';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { CountryAndIndustries } from 'shared/types/shop';
import { useQuery } from 'shared/hooks';
import { orderTypes } from 'shared/constants/orderTypes';
import { InfoTooltip, ScoreChangeIndicator } from 'ui';
import { LinkButton } from 'ui/Buttons';
import { RequestButton, RequestEsgButton } from 'modules/common';
import { setRatingColor, formatDate } from 'shared/functions';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { outlookLabels } from 'shared/constants/outlookTypes';
import { activityMappingCreditRating } from 'shared/mappings/activityLabels';
import { Button, Grid, IconButton } from '@mui/material';
import { RatingStatus } from 'shared/types/ratingStatus';
import { Score } from 'shared/types/score';
import clsx from 'clsx';
import { Box } from '@scope/scope-one-ui/core';
import Check from '@scope/scope-one-ui/assets/icons/check.svg';
import X from '@scope/scope-one-ui/assets/icons/x.svg';
import { COLORS, SIZES } from '@scope/scope-one-ui/theme';
import { OrangeRatingChip } from '../../../../shared/styles/OrangeRatingChip/OrangeRatingChip';

const useStyles = makeStyles((theme: Theme) => ({
  tooltip: {
    marginLeft: 3,
    marginBottom: 3,
  },
  chip: {
    color: theme.palette.custom.white,
    backgroundColor: theme.palette.custom.darkGrayAlt,
  },
  totalChip: {
    color: theme.palette.custom.white,
    backgroundColor: theme.palette.custom.primary,
  },
  requestCell: {
    backgroundColor: grey[100],
  },
  statusInfo: {
    position: 'relative',
    '& > span': {
      width: '12rem',
      textAlign: 'center',
      position: 'absolute',
      whiteSpace: 'nowrap',
      zIndex: 1,
      transform: 'translate(-30%, -50%)',
    },
  },
  cstLabel: {
    gridGap: theme.spacing(1),
  },
  infoIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    color: theme.palette.primary.main,
  },
}));

type Scenario = 'hotHouse' | 'orderly' | 'disorderly';

type UseEntitiesTableColumnsProps = {
  ratingType: ratingTypes;
  isPortfolioTable?: boolean;
  isRequestEnabled?: boolean;
  isUnlockEnabled?: boolean;
  onRequestClick: (ids: string[]) => unknown;
  onUnlockClick: (countriesAndIndustries?: CountryAndIndustries[]) => unknown;
  isESGScoringTool?: boolean;
  hasESGScoringToolSubscription?: boolean;
};

type RatingStateProps = {
  value?: boolean;
};

const RatingState: FC<RatingStateProps> = ({ value }) => {
  if (value)
    return (
      <Check width={SIZES.spacing(2)} height={SIZES.spacing(2)} stroke={COLORS.success.main} />
    );

  return <X width={SIZES.spacing(1.5)} height={SIZES.spacing(1.5)} stroke={COLORS.error.main} />;
};

const useEntitiesTableColumns = ({
  ratingType,
  isPortfolioTable,
  isRequestEnabled,
  isUnlockEnabled,
  onRequestClick,
  onUnlockClick,
  isESGScoringTool,
}: UseEntitiesTableColumnsProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { module } = useQuery();

  const getOptionsForClimateStressTest = () => ({
    customBodyRender: ([item, scenario]) => {
      return item;
    },
  });

  const cstTooltipRenderer = ({ index, ...column }) => (
    <Tooltip key={index} placement="top" title={<>{t('Yearly Loss (Ave.) % - (2022-2050)')}</>}>
      <Box display="flex" alignItems="center" className={classes.cstLabel}>
        {column.label}
        <InfoIcon className={classes.infoIcon} />
      </Box>
    </Tooltip>
  );

  const renderClimateLossScore = (
    score: Record<Scenario, Record<string, number>>,
    scenario: Scenario,
    scoreName: string,
  ) => {
    if (!scenario || !score[scenario]) {
      return <></>;
    }

    return `${score[scenario][scoreName].toFixed(2)}%`;
  };

  const getSentimentOptions = (column: Score) => ({
    sort: true,
    setCellProps: ({ props }: any) => ({ className: !props.label && classes.requestCell }),
    customBodyRender: ([score, hasPermission, hasCompany, hasScore]: any) => {
      const isTotalScoreColumn = column === Score.TOTAL;

      if (!hasScore) {
        return <>N/A</>;
      }

      if (!hasPermission && isTotalScoreColumn) {
        return (
          <Button color="primary" variant="contained" onClick={() => onUnlockClick()}>
            {t('UNLOCK')}
          </Button>
        );
      }

      if (!hasCompany && isTotalScoreColumn) {
        return <RequestEsgButton onRequestClick={() => onRequestClick([score.esgId])} />;
      }

      const label = isTotalScoreColumn ? score.totalScore : score;

      if (label === '-') {
        return <>{label}</>;
      }

      return (
        <Chip color={isTotalScoreColumn ? 'primary' : 'default'} label={label} variant="outlined" />
      );
    },
  });

  const getImpactOptions = (column: Score) => ({
    sort: true,
    setCellProps: ({ props }: any) => ({ className: !props.label && classes.requestCell }),
    customBodyRender: ([score, state]: any) => {
      const isSocialColumn = column === Score.SOCIAL;
      const isTotalScoreColumn = column === Score.TOTAL;
      const customScore = state === RatingStatus.DONE;
      const requiresScoring = state === RatingStatus.IN_PROGRESS || state === RatingStatus.TO_DO;

      const unableToRequest = state === RatingStatus.UNABLE_TO_REQUEST;

      if (isESGScoringTool && (requiresScoring || unableToRequest)) {
        if (isSocialColumn) {
          return (
            <div className={classes.statusInfo}>
              <span>
                {state === RatingStatus.TO_DO && t('ESG.MISSING_FINANCIAL_STATEMENT')}
                {state === RatingStatus.UNABLE_TO_REQUEST && t('ESG.ONLY_AVAILABLE_FOR_CORPORATES')}
                {state === RatingStatus.IN_PROGRESS && t('IN_PROGRESS')}
              </span>
            </div>
          );
        }

        if (isTotalScoreColumn && isRequestEnabled && state === RatingStatus.IN_PROGRESS) {
          return <RequestEsgButton alreadyRequested />;
        }

        if (isTotalScoreColumn && isRequestEnabled && state === RatingStatus.TO_DO) {
          return <RequestEsgButton onRequestClick={() => onRequestClick([score.esgId])} />;
        }

        if (
          isTotalScoreColumn &&
          isUnlockEnabled &&
          !isRequestEnabled &&
          state === RatingStatus.TO_DO
        ) {
          return (
            <Button color="primary" variant="contained" onClick={() => onUnlockClick()}>
              {t('UNLOCK')}
            </Button>
          );
        }

        return <></>;
      }

      const label = isTotalScoreColumn ? score.totalScore : score;

      if (label === '-') {
        return <>{label}</>;
      }

      return (
        <Chip
          color={isTotalScoreColumn ? 'primary' : 'default'}
          label={label}
          variant={customScore ? 'filled' : 'outlined'}
          className={clsx(customScore && (isTotalScoreColumn ? classes.totalChip : classes.chip))}
        />
      );
    },
  });

  const columnsByRatingType: Partial<Record<ratingTypes, MUIDataTableColumn[]>> = {
    [ratingTypes.CREDIT_RATING]: [
      { name: 'extendedData', label: t('ENTITY.EXTENDED_DATA'), options: { display: 'excluded' } },
      {
        name: 'name',
        label: t('ENTITY.NAME'),
        options: {
          sort: true,
          customBodyRender: (v: any) => (
            <LinkButton to={`entities/${v[0]}?module=credit`} label={v[1]} withPathname />
          ),
        },
      },
      { name: 'lei', label: t('ENTITY.LEI'), options: { sort: true } },
      { name: 'countryCode', label: t('ENTITY.COUNTRY'), options: { sort: true } },
      { name: 'industry', label: t('ENTITY.INDUSTRY'), options: { sort: true } },
      {
        name: 'ultimateIssuerName',
        label: t('ENTITY.ULTIMATE_PARENT'),
        options: {
          sort: true,
          customBodyRender: (v: any) =>
            v && <LinkButton to={`entities/${v[0]}?module=credit`} label={v[1]} withPathname />,
        },
      },
      {
        name: 'ratingType',
        label: t('ENTITY.RATING_TYPE'),
        options: {
          display: false,
          sort: false,
        },
      },
      {
        name: 'demandType',
        label: t('ENTITY.DEMAND_TYPE'),
        options: {
          display: false,
          sort: false,
        },
      },
      {
        name: 'entityRating',
        label: t('ENTITY.ENTITY_RATING'),
        options: {
          display: false,
          sort: false,
          customBodyRender: (v: any) => {
            const [
              id,
              product,
              rating,
              outlook,
              hasLicence,
              ratingTrend,
              previousRating,
              previousOutlook,
              longTermRatingDate,
              countriesAndIndustries,
              demandType,
            ] = v;
            const isUnsolicited = demandType?.includes('Unsolicited without participation');
            let body: React.ReactElement | null = null;
            const tooltip = (
              <>
                <div>
                  {t('Rating')}: {rating}
                </div>
                <div>{outlook ? `${t('Outlook')}: ${t(outlookLabels[outlook])}` : ''}</div>
                {ratingTrend && (
                  <>
                    <div>
                      {t('ENTITY.PREVIOUS_RATING')}: {previousRating}
                    </div>
                    <div>
                      {t('ENTITY.PREVIOUS_OUTLOOK')}: {previousOutlook}
                    </div>
                    <div>
                      {t('ENTITY.CHANGE_AS_OF_LATEST_RATING_ACTION')}:
                      {formatDate(longTermRatingDate)}
                    </div>
                  </>
                )}
              </>
            );

            /* 
              Special case: "There are duplicates in storefront db and we agreed
              that if there is no industry we just show the request button."

              https://jira.scopegroup.com/browse/SCI-5083 
            */
            const hasIndustry = countriesAndIndustries?.[0].industries.length!!;

            if (rating) {
              body = (
                <Tooltip title={tooltip}>
                  {isUnsolicited ? (
                    <OrangeRatingChip label={rating} variant="outlined" />
                  ) : (
                    <Chip label={rating} variant="outlined" color={setRatingColor(outlook)} />
                  )}
                </Tooltip>
              );
            } else if (!hasLicence && hasIndustry) {
              body = (
                <IconButton
                  color="inherit"
                  onClick={() => onUnlockClick(countriesAndIndustries)}
                  size="large"
                >
                  <LockIcon />
                </IconButton>
              );
            } else if (product) {
              body = <RequestButton id={id} product={product} orderType={orderTypes.ENTITY} />;
            }
            return body;
          },
        },
      },
      {
        name: 'shortTermRating',
        label: t('ENTITY.SHORT_TERM_RATING'),
        options: {
          display: false,
          sort: false,
          customBodyRender: (v: any) => {
            const [label, ratingTrend, previousRating, shortTermRatingDate, demandType] = v;
            const isUnsolicited = demandType?.includes('Unsolicited without participation');
            if (!label) return <></>;

            const tooltip = (
              <>
                <div>
                  {t('ENTITY.SHORT_TERM_RATING')}: {label}
                </div>
                {ratingTrend && (
                  <>
                    <div>
                      {t('ENTITY.PREVIOUS_RATING')}: {previousRating}
                    </div>
                    <div>
                      {t('ENTITY.CHANGE_AS_OF_LATEST_RATING_ACTION')}:{' '}
                      {formatDate(shortTermRatingDate)}
                    </div>
                  </>
                )}
              </>
            );

            const content = isUnsolicited ? (
              <OrangeRatingChip label={label} variant="outlined" />
            ) : (
              <Chip label={label} variant="outlined" />
            );
            return ratingTrend ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
          },
        },
      },
      {
        name: 'publicRating',
        label: t('ENTITY.PUBLIC_RATING'),
        options: {
          sort: true,
          setCellProps: () => ({ style: { textAlign: 'center' } }),
          customBodyRender: (value) => <RatingState value={value} />,
        },
      },
      {
        name: 'subscriptionRating',
        label: t('ENTITY.SUBSCRIPTION_RATING'),
        options: {
          sort: true,
          setCellProps: () => ({ style: { textAlign: 'center' } }),
          customBodyRender: (value) => <RatingState value={value} />,
        },
      },
      {
        name: 'restrictedSubscriptionRating',
        label: t('ENTITY.RESTRICTED_SUBSCRIPTION_RATING'),
        options: {
          sort: true,
          setCellProps: () => ({ style: { textAlign: 'center' } }),
          customBodyRender: (value) => <RatingState value={value} />,
        },
      },
      {
        name: 'addedDate',
        label: t('ENTITY.ADDED'),
        options: {
          display: isPortfolioTable,
          sort: true,
          customBodyRender: (value: any) => (value ? formatDate(value, 'dd/MM/yyyy') : '-'),
        },
      },
      {
        name: 'activity',
        label: t('ENTITY.ACTIVITY'),
        options: {
          display: isPortfolioTable,
          sort: true,
          sortOrder: 'asc',
          hint: t('ENTITY.ACTIVITY_HINT'),
          customBodyRender: ([
            longTermRatingTrend,
            previousLongTermRating,
            longTermRatingDate,
          ]: any) => {
            return longTermRatingTrend ? (
              <ScoreChangeIndicator
                activity={longTermRatingTrend}
                activityMapping={activityMappingCreditRating}
                scoreChangeDate={longTermRatingDate}
                scoreOld={previousLongTermRating}
                showLabel
              />
            ) : null;
          },
        } as any,
      },
    ],
    [ratingTypes.ESG_IMPACT]: [
      {
        name: 'id',
        label: t('Id'),
        options: { sort: false, display: false, viewColumns: false },
      },
      {
        name: 'companyName',
        label: t('Entity Name'),
        options: {
          sort: true,
          customBodyRender: ([companyName, state]: any, { rowData: [entityId] }: any) => {
            const customScore = state === RatingStatus.DONE;

            return (
              <Grid container alignItems="center">
                <LinkButton
                  to={`entities/${entityId}?companyModule=esg-impact&module=esg`}
                  label={companyName.toUpperCase()}
                  withPathname
                />
                {customScore && (
                  <InfoTooltip
                    title={t('ESG.CUSTOM_SCORE_TOOLTIP')}
                    iconClassName={classes.tooltip}
                  />
                )}
              </Grid>
            );
          },
        },
      },
      { name: 'companyCountry', label: t('Country'), options: { sort: true } },
      { name: 'industry', label: t('Industry'), options: { sort: true } },
      {
        name: 'environmentScore',
        label: t('Environment'),
        options: getImpactOptions(Score.ENVIRONMENT),
      },
      { name: 'socialScore', label: t('Social'), options: getImpactOptions(Score.SOCIAL) },
      {
        name: 'governanceScore',
        label: t('Governance'),
        options: getImpactOptions(Score.GOVERNANCE),
      },
      { name: 'totalScore', label: t('Total Score'), options: getImpactOptions(Score.TOTAL) },
      {
        name: 'wasPurchased',
        label: t('Subscription'),
        options: {
          sort: false,
          customBodyRender: (value: any) => (value ? <LockOpenIcon /> : <LockIcon />),
        },
      },
      { name: 'ids', label: t('ENTITY.EXTENDED_DATA'), options: { display: 'excluded' } },
    ],
    [ratingTypes.SENTIMENT]: [
      {
        name: 'id',
        label: t('Id'),
        options: { sort: false, display: false, viewColumns: false },
      },
      {
        name: 'companyName',
        label: t('Entity Name'),
        options: {
          sort: true,
          customBodyRender: ([companyName]: any, { rowData: [entityId] }: any) => (
            <Grid container alignItems="center">
              <LinkButton
                to={`entities/${entityId}?companyModule=esg-sentiment&module=esg`}
                label={companyName.toUpperCase()}
                withPathname
              />
            </Grid>
          ),
        },
      },
      { name: 'companyCountry', label: t('Country'), options: { sort: true } },
      { name: 'industry', label: t('Industry'), options: { sort: true } },
      {
        name: 'environmentScore',
        label: t('Environment'),
        options: getSentimentOptions(Score.ENVIRONMENT),
      },
      { name: 'socialScore', label: t('Social'), options: getSentimentOptions(Score.SOCIAL) },
      {
        name: 'governanceScore',
        label: t('Governance'),
        options: getSentimentOptions(Score.GOVERNANCE),
      },
      { name: 'totalScore', label: t('Total Score'), options: getSentimentOptions(Score.TOTAL) },
      {
        name: 'wasPurchased',
        label: t('Subscription'),
        options: {
          sort: false,
          customBodyRender: (value: any) => (value ? <LockOpenIcon /> : <LockIcon />),
        },
      },
      { name: 'ids', label: t('ENTITY.EXTENDED_DATA'), options: { display: 'excluded' } },
    ],
    [ratingTypes.CLIMATE_STRESS_TEST]: [
      {
        name: 'id',
        label: t('Id'),
        options: { sort: false, display: false, viewColumns: false },
      },
      {
        name: 'name',
        label: t('Entity Name'),
        options: {
          customBodyRender: ([item]) => (
            <LinkButton
              to={`entities/${item.id}?companyModule=climate-stress-test&module=esg`}
              label={item.name.toUpperCase()}
              withPathname
            />
          ),
        },
      },
      { name: 'country', label: t('Country'), options: getOptionsForClimateStressTest() },
      {
        name: 'industry',
        label: t('Industry'),
        options: getOptionsForClimateStressTest(),
      },
      {
        name: 'climateLoss',
        label: t('PORTFOLIO.CLIMATE_LOSS'),
        options: {
          customHeadLabelRender: cstTooltipRenderer,
          customBodyRender: ([score, scenario]) =>
            renderClimateLossScore(score, scenario, 'climateLoss'),
        },
      },
      {
        name: 'physicalRiskLoss',
        label: t('PORTFOLIO.PHYSICAL_RISK_LOSS'),
        options: {
          customHeadLabelRender: cstTooltipRenderer,
          customBodyRender: ([score, scenario]) =>
            renderClimateLossScore(score, scenario, 'physicalRiskLoss'),
        },
      },
      {
        name: 'transitionRiskLoss',
        label: t('PORTFOLIO.TRANSITION_RISK_LOSS'),
        options: {
          customHeadLabelRender: cstTooltipRenderer,
          customBodyRender: ([score, scenario]) =>
            renderClimateLossScore(score, scenario, 'transitionRiskLoss'),
        },
      },
    ],
  };

  const buildInitialColumns = useCallback(
    () => columnsByRatingType[ratingType],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ratingType, isPortfolioTable],
  );

  const [initialColumns, setInitialColumns] = useState(buildInitialColumns());

  useEffect(() => {
    setInitialColumns(buildInitialColumns());
  }, [buildInitialColumns]);

  return initialColumns;
};

export default useEntitiesTableColumns;
